import type { SupabaseClient } from "@supabase/supabase-js";
import { DatabaseConflictError, throwDatabaseError, withoutUndefined } from "../lib/database";
import { inputFollowUps, inputOrders } from "../lib/inputCompatibility";
import type {
  Activity,
  ActivityChannelInput,
  ActivityEditInput,
  AttachmentInput,
  AuditEvent,
  AuditEventPage,
  BreakGlassVendorRow,
  CaptureBundleInput,
  CaptureBundleResult,
  Contact,
  ContactDirectoryEntry,
  ContactInput,
  CrmAttachment,
  CrmInput,
  DashboardData,
  DedupeCandidate,
  DeleteRecordType,
  FollowUp,
  FollowUpDetail,
  FollowUpEditInput,
  FollowUpLifecycleInput,
  MemberRole,
  Opportunity,
  OpportunityInput,
  Order,
  OrderDetail,
  OrderEditInput,
  Quote,
  QuoteInput,
  SampleRequest,
  SampleRequestInput,
  TeamMember,
  UserProfile,
  Vendor,
  VendorCrmProfileInput,
  VendorDetail,
  VendorInput,
  VendorMatch,
  VendorMatchInput,
} from "../types";
import { addActivityAuthors } from "./activityAuthors";
import type { Repository } from "./Repository";

type ActivityRow = Omit<Activity, "author">;

type Membership = {
  organizationId: string;
  role: MemberRole;
  userId: string;
};

const authenticationChanged = Symbol("authentication-changed");

function required<T>(data: T | null, error: { code?: string; message: string } | null): T {
  throwDatabaseError(error);
  if (data === null) throw new Error("The database returned no data");
  return data;
}

function versioned<T>(data: T | null, error: { code?: string; message: string } | null): T {
  throwDatabaseError(error);
  if (data === null) throw new DatabaseConflictError();
  return data;
}

export class SupabaseRepository implements Repository {
  private membershipPromise: Promise<Membership> | null = null;
  private membershipGeneration = 0;

  constructor(private readonly client: SupabaseClient) {
    const onAuthStateChange = this.client.auth?.onAuthStateChange;
    if (typeof onAuthStateChange === "function") {
      onAuthStateChange.call(this.client.auth, async () => {
        this.membershipGeneration += 1;
        this.membershipPromise = null;
      });
    }
  }

  private async getMembership(): Promise<Membership> {
    for (;;) {
      if (!this.membershipPromise) {
        const generation = this.membershipGeneration;
        let membershipRequest: Promise<Membership>;
        membershipRequest = (async () => {
          const {
            data: { user },
            error: userError,
          } = await this.client.auth.getUser();
          if (userError) throw new Error(userError.message);
          if (!user) throw new Error("Authentication required");

          const { data, error } = await this.client
            .from("organization_members")
            .select("organization_id,role")
            .eq("user_id", user.id)
            .eq("active", true)
            .limit(1)
            .single();
          const membership = required(data, error);
          if (generation !== this.membershipGeneration) throw authenticationChanged;
          return {
            organizationId: membership.organization_id as string,
            role: membership.role as MemberRole,
            userId: user.id,
          };
        })().catch((error: unknown) => {
          if (this.membershipPromise === membershipRequest) this.membershipPromise = null;
          throw error;
        });
        this.membershipPromise = membershipRequest;
      }

      try {
        return await this.membershipPromise;
      } catch (error) {
        if (error !== authenticationChanged) throw error;
      }
    }
  }

  private async getOrganizationId(): Promise<string> {
    return (await this.getMembership()).organizationId;
  }

  async getCurrentRole(): Promise<MemberRole> {
    return (await this.getMembership()).role;
  }

  private async getProfiles(): Promise<UserProfile[]> {
    const { data, error } = await this.client.from("profiles").select("user_id,display_name");
    return required(data, error) as UserProfile[];
  }

  private async addAuthor(activity: ActivityRow): Promise<Activity> {
    const profiles = await this.getProfiles();
    const attributed = addActivityAuthors([activity], profiles)[0];
    if (!attributed) throw new Error("Activity attribution failed");
    return attributed;
  }

  private async saveVersionedVendorRecord<T>(
    table: string,
    vendorId: string,
    id: string | null,
    expectedVersion: number | null,
    payload: Record<string, unknown>,
  ): Promise<T> {
    if (id === null) {
      const organizationId = await this.getOrganizationId();
      const { data, error } = await this.client
        .from(table)
        .insert({ ...payload, vendor_id: vendorId, organization_id: organizationId })
        .select("*")
        .single();
      return required(data, error) as T;
    }
    if (expectedVersion === null) throw new Error("A record version is required for updates");
    const updateRpc = {
      opportunities: { name: "update_opportunity", idKey: "p_opportunity_id" },
      sample_requests: { name: "update_sample_request", idKey: "p_sample_request_id" },
      quotes: { name: "update_quote", idKey: "p_quote_id" },
    }[table];
    if (!updateRpc) throw new Error(`Unsupported versioned CRM table: ${table}`);
    const { data, error } = await this.client.rpc(updateRpc.name, {
      p_vendor_id: vendorId,
      [updateRpc.idKey]: id,
      p_expected_version: expectedVersion,
      p_input: payload,
    });
    return versioned(data, error) as T;
  }

  async getDashboard(): Promise<DashboardData> {
    const [organizationId, profiles] = await Promise.all([
      this.getOrganizationId(),
      this.getProfiles(),
    ]);
    const horizon = new Date();
    horizon.setDate(horizon.getDate() + 30);

    const dayStart = new Date();
    dayStart.setHours(0, 0, 0, 0);
    const [activitiesResult, followUpsResult, ordersResult, vendorCountResult, notesResult] =
      await Promise.all([
        this.client
          .from("activities")
          .select("*, vendor:vendors!inner(id,trading_name), orders(id)")
          .eq("organization_id", organizationId)
          .order("occurred_at", { ascending: false })
          .limit(12),
        (async () => {
          const rows: DashboardData["dueFollowUps"] = [];
          for (let offset = 0; ; offset += 500) {
            const result = await this.client
              .from("follow_ups")
              .select("*, vendor:vendors!inner(id,trading_name)")
              .eq("organization_id", organizationId)
              .eq("status", "open")
              .lte("due_at", horizon.toISOString())
              .order("due_at")
              .order("id")
              .range(offset, offset + 499);
            if (result.error) return { data: null, error: result.error };
            rows.push(...(result.data as DashboardData["dueFollowUps"]));
            if (result.data.length < 500) return { data: rows, error: null };
          }
        })(),
        (async () => {
          const orders: DashboardData["pendingOrders"] = [];
          for (let offset = 0; ; offset += 500) {
            const result = await this.client
              .from("orders")
              .select("*, vendor:vendors!inner(id,trading_name)")
              .eq("organization_id", organizationId)
              .in("status", ["pending", "confirmed"])
              .order("id")
              .range(offset, offset + 499);
            if (result.error) return { data: null, error: result.error };
            orders.push(...(result.data as DashboardData["pendingOrders"]));
            if (result.data.length < 500)
              return {
                data: orders.sort((a, b) => b.created_at.localeCompare(a.created_at)),
                error: null,
              };
          }
        })(),
        this.client
          .from("vendors")
          .select("id", { count: "exact", head: true })
          .eq("organization_id", organizationId),
        this.client
          .from("activities")
          .select("id", { count: "exact", head: true })
          .eq("organization_id", organizationId)
          .gte("occurred_at", dayStart.toISOString()),
      ]);

    if (activitiesResult.error) throw new Error(activitiesResult.error.message);
    if (followUpsResult.error) throw new Error(followUpsResult.error.message);
    if (ordersResult.error) throw new Error(ordersResult.error.message);
    if (notesResult.error) throw new Error(notesResult.error.message);
    if (vendorCountResult.error) throw new Error(vendorCountResult.error.message);

    const recentActivities = addActivityAuthors(activitiesResult.data ?? [], profiles).map(
      (activity) => {
        const row = activity as typeof activity & { orders?: Array<{ id: string }> };
        const { orders, ...record } = row;
        return {
          ...record,
          order_id:
            record.activity_type === "order" && orders && orders.length > 0
              ? (orders[0]?.id ?? null)
              : null,
        };
      },
    ) as DashboardData["recentActivities"];

    return {
      recentActivities,
      dueFollowUps: (followUpsResult.data ?? []) as DashboardData["dueFollowUps"],
      pendingOrders: (ordersResult.data ?? []) as DashboardData["pendingOrders"],
      vendorCount: vendorCountResult.count ?? 0,
      notesToday: notesResult.count ?? 0,
    };
  }

  async searchVendors(query: string): Promise<Vendor[]> {
    const { data, error } = await this.client.rpc("search_vendors", {
      p_search: query.trim(),
      p_limit: 100,
    });
    return required(data, error) as Vendor[];
  }

  async searchContacts(query: string, limit = 8): Promise<ContactDirectoryEntry[]> {
    const clean = query.trim();
    if (!clean) return [];
    const organizationId = await this.getOrganizationId();
    const { data, error } = await this.client
      .from("contacts")
      .select("*, vendor:vendors!contacts_vendor_org_fk(*)")
      .eq("organization_id", organizationId)
      .ilike("name", `%${clean}%`)
      .order("priority", { ascending: true, nullsFirst: false })
      .order("name", { ascending: true })
      .limit(Math.min(Math.max(limit, 1), 50));
    type ContactDirectoryRow = Contact & { vendor: Vendor | Vendor[] | null };
    const rows = required(data, error) as unknown as ContactDirectoryRow[];
    return rows.flatMap((row) => {
      const { vendor: relatedVendor, ...contact } = row;
      const vendor = Array.isArray(relatedVendor) ? relatedVendor[0] : relatedVendor;
      return vendor ? [{ contact: contact as Contact, vendor }] : [];
    });
  }

  async matchVendors(input: VendorMatchInput): Promise<VendorMatch[]> {
    const { data, error } = await this.client.rpc("match_vendors", {
      p_name: input.name.trim(),
      p_location: input.locationText?.trim() || null,
      p_latitude: input.latitude ?? null,
      p_longitude: input.longitude ?? null,
      p_limit: input.limit ?? 8,
    });
    return required(data, error) as VendorMatch[];
  }

  async getVendor(id: string): Promise<VendorDetail> {
    const [membership, profiles] = await Promise.all([this.getMembership(), this.getProfiles()]);
    const [
      vendorResult,
      contactsResult,
      activitiesResult,
      ordersResult,
      followUpsResult,
      opportunitiesResult,
      samplesResult,
      quotesResult,
      attachmentsResult,
      dedupeResult,
      membersResult,
    ] = await Promise.all([
      this.client.from("vendors").select("*").eq("id", id).single(),
      this.client.from("contacts").select("*").eq("vendor_id", id).order("priority"),
      this.client
        .from("activities")
        .select("*")
        .eq("vendor_id", id)
        .order("occurred_at", { ascending: false }),
      this.client
        .from("orders")
        .select("*")
        .eq("vendor_id", id)
        .order("created_at", { ascending: false }),
      this.client
        .from("follow_ups")
        .select("*")
        .eq("vendor_id", id)
        .order("due_at", { ascending: false }),
      this.client
        .from("opportunities")
        .select("*")
        .eq("vendor_id", id)
        .order("updated_at", { ascending: false }),
      this.client
        .from("sample_requests")
        .select("*")
        .eq("vendor_id", id)
        .order("updated_at", { ascending: false }),
      this.client
        .from("quotes")
        .select("*")
        .eq("vendor_id", id)
        .order("updated_at", { ascending: false }),
      this.client
        .from("crm_attachments")
        .select("*")
        .eq("vendor_id", id)
        .order("created_at", { ascending: false }),
      this.client
        .from("vendor_dedupe_candidates")
        .select(
          "*, vendor:vendors!vendor_dedupe_vendor_org_fk(id,trading_name,location_name,suburb), candidate_vendor:vendors!vendor_dedupe_candidate_org_fk(id,trading_name,location_name,suburb)",
        )
        .or(`vendor_id.eq.${id},candidate_vendor_id.eq.${id}`)
        .eq("status", "pending")
        .order("confidence", { ascending: false }),
      this.client
        .from("organization_members")
        .select("user_id,role")
        .eq("organization_id", membership.organizationId)
        .eq("active", true),
    ]);

    const profileById = new Map(profiles.map((profile) => [profile.user_id, profile]));
    const teamMembers = required(membersResult.data, membersResult.error).map((member) => ({
      user_id: member.user_id as string,
      display_name: profileById.get(member.user_id as string)?.display_name ?? "Team member",
      role: member.role as MemberRole,
    })) satisfies TeamMember[];

    return {
      vendor: required(vendorResult.data, vendorResult.error) as Vendor,
      contacts: required(contactsResult.data, contactsResult.error),
      activities: addActivityAuthors(
        required(activitiesResult.data, activitiesResult.error),
        profiles,
      ),
      orders: required(ordersResult.data, ordersResult.error),
      followUps: required(followUpsResult.data, followUpsResult.error),
      opportunities: required(opportunitiesResult.data, opportunitiesResult.error),
      sampleRequests: required(samplesResult.data, samplesResult.error),
      quotes: required(quotesResult.data, quotesResult.error),
      attachments: required(attachmentsResult.data, attachmentsResult.error),
      dedupeCandidates: required(dedupeResult.data, dedupeResult.error),
      teamMembers,
    } as VendorDetail;
  }

  async getOrder(id: string): Promise<OrderDetail> {
    const { data, error } = await this.client
      .from("orders")
      .select("*, vendor:vendors!inner(id,trading_name)")
      .eq("id", id)
      .single();
    return required(data, error) as OrderDetail;
  }

  async getFollowUp(id: string): Promise<FollowUpDetail> {
    const { data, error } = await this.client
      .from("follow_ups")
      .select("*, vendor:vendors!inner(id,trading_name)")
      .eq("id", id)
      .single();
    return required(data, error) as FollowUpDetail;
  }

  async createVendor(input: VendorInput, contact?: ContactInput): Promise<Vendor> {
    const organizationId = await this.getOrganizationId();
    const cleaned = Object.fromEntries(
      Object.entries(input).map(([key, value]) => [key, value === "" ? null : value]),
    );
    if (contact) {
      const { data, error } = await this.client.rpc("create_vendor_with_contact", {
        p_organization_id: organizationId,
        p_vendor_input: cleaned,
        p_contact_input: withoutUndefined(contact),
      });
      return required(data, error) as Vendor;
    }

    const { data, error } = await this.client
      .from("vendors")
      .insert({
        ...cleaned,
        organization_id: organizationId,
        location_confirmed_at: input.location_source ? new Date().toISOString() : null,
      })
      .select("*")
      .single();
    return required(data, error) as Vendor;
  }

  async updateVendor(id: string, expectedVersion: number, input: VendorInput): Promise<Vendor> {
    const { data, error } = await this.client.rpc("update_vendor", {
      p_vendor_id: id,
      p_expected_version: expectedVersion,
      p_input: input,
    });
    return versioned(data, error) as Vendor;
  }

  async updateVendorCrmProfile(
    id: string,
    expectedVersion: number,
    input: VendorCrmProfileInput,
  ): Promise<Vendor> {
    const { data, error } = await this.client.rpc("update_vendor_crm_profile", {
      p_vendor_id: id,
      p_expected_version: expectedVersion,
      p_input: input,
    });
    return versioned(data, error) as Vendor;
  }

  async updateActivity(
    id: string,
    expectedVersion: number,
    input: ActivityEditInput,
  ): Promise<Activity> {
    const { data, error } = await this.client.rpc("update_activity", {
      p_activity_id: id,
      p_expected_version: expectedVersion,
      p_input: input,
    });
    return this.addAuthor(versioned(data, error) as ActivityRow);
  }

  async updateActivityChannel(
    id: string,
    expectedVersion: number,
    input: ActivityChannelInput,
  ): Promise<Activity> {
    const { data, error } = await this.client.rpc("update_activity_channel", {
      p_activity_id: id,
      p_expected_version: expectedVersion,
      p_channel: input.channel,
      p_direction: input.direction,
    });
    return this.addAuthor(versioned(data, error) as ActivityRow);
  }

  async updateOrder(id: string, expectedVersion: number, input: OrderEditInput): Promise<Order> {
    const { data, error } = await this.client.rpc("update_order", {
      p_order_id: id,
      p_expected_version: expectedVersion,
      p_input: input,
    });
    return versioned(data, error) as Order;
  }

  async updateFollowUp(
    id: string,
    expectedVersion: number,
    input: FollowUpEditInput,
  ): Promise<FollowUp> {
    const { data, error } = await this.client.rpc("update_follow_up", {
      p_follow_up_id: id,
      p_expected_version: expectedVersion,
      p_input: input,
    });
    return versioned(data, error) as FollowUp;
  }

  async updateFollowUpLifecycle(
    id: string,
    expectedVersion: number,
    input: FollowUpLifecycleInput,
  ): Promise<FollowUp> {
    const { data, error } = await this.client.rpc("update_follow_up_lifecycle", {
      p_follow_up_id: id,
      p_expected_version: expectedVersion,
      p_action: input.action,
      p_due_at: input.due_at ?? null,
      p_assigned_to: input.assigned_to ?? null,
    });
    return versioned(data, error) as FollowUp;
  }

  async saveOpportunity(
    vendorId: string,
    id: string | null,
    expectedVersion: number | null,
    input: OpportunityInput,
  ): Promise<Opportunity> {
    const membership = await this.getMembership();
    const isClosed = input.stage === "won" || input.stage === "lost";
    return this.saveVersionedVendorRecord<Opportunity>(
      "opportunities",
      vendorId,
      id,
      expectedVersion,
      withoutUndefined({
        title: input.title.trim(),
        stage: input.stage,
        estimated_value: input.estimated_value ?? null,
        currency: (input.currency ?? "AUD").toUpperCase(),
        probability: input.probability ?? (input.stage === "won" ? 100 : 10),
        expected_close_date: input.expected_close_date || null,
        next_step: input.next_step?.trim() || null,
        owner_id: input.owner_id || membership.userId,
        closed_at: isClosed ? new Date().toISOString() : null,
        lost_reason: input.stage === "lost" ? input.lost_reason?.trim() || null : null,
      }),
    );
  }

  async saveSampleRequest(
    vendorId: string,
    id: string | null,
    expectedVersion: number | null,
    input: SampleRequestInput,
  ): Promise<SampleRequest> {
    const membership = await this.getMembership();
    const now = new Date().toISOString();
    const wasSent = ["sent", "delivered", "feedback", "closed"].includes(input.status);
    const wasDelivered = ["delivered", "feedback", "closed"].includes(input.status);
    return this.saveVersionedVendorRecord<SampleRequest>(
      "sample_requests",
      vendorId,
      id,
      expectedVersion,
      withoutUndefined({
        opportunity_id: input.opportunity_id || null,
        product_id: input.product_id || null,
        description: input.description.trim(),
        quantity: input.quantity ?? null,
        unit: input.unit?.trim() || null,
        status: input.status,
        sent_at: wasSent ? now : null,
        delivered_at: wasDelivered ? now : null,
        feedback: input.feedback?.trim() || null,
        owner_id: input.owner_id || membership.userId,
      }),
    );
  }

  async saveQuote(
    vendorId: string,
    id: string | null,
    expectedVersion: number | null,
    input: QuoteInput,
  ): Promise<Quote> {
    const membership = await this.getMembership();
    const now = new Date().toISOString();
    const wasSent = ["sent", "accepted", "rejected", "expired"].includes(input.status);
    return this.saveVersionedVendorRecord<Quote>(
      "quotes",
      vendorId,
      id,
      expectedVersion,
      withoutUndefined({
        opportunity_id: input.opportunity_id || null,
        quote_reference: input.quote_reference?.trim() || null,
        summary: input.summary.trim(),
        amount: input.amount ?? null,
        currency: (input.currency ?? "AUD").toUpperCase(),
        status: input.status,
        valid_until: input.valid_until || null,
        sent_at: wasSent ? now : null,
        accepted_at: input.status === "accepted" ? now : null,
        owner_id: input.owner_id || membership.userId,
      }),
    );
  }

  async saveAttachmentMetadata(vendorId: string, input: AttachmentInput): Promise<CrmAttachment> {
    const organizationId = await this.getOrganizationId();
    const { data, error } = await this.client
      .from("crm_attachments")
      .insert({
        ...withoutUndefined(input),
        vendor_id: vendorId,
        organization_id: organizationId,
        mime_type: input.mime_type || null,
        byte_size: input.byte_size ?? null,
        metadata: input.metadata ?? {},
      })
      .select("*")
      .single();
    return required(data, error) as CrmAttachment;
  }

  async refreshDedupeCandidates(vendorId?: string): Promise<number> {
    const organizationId = await this.getOrganizationId();
    const { data, error } = await this.client.rpc("refresh_vendor_dedupe_candidates", {
      p_vendor_id: vendorId ?? null,
      p_threshold: 0.62,
      p_organization_id: organizationId,
    });
    return required(data, error) as number;
  }

  async resolveDedupeCandidate(
    id: string,
    expectedVersion: number,
    resolution: "not_duplicate" | "merge_planned",
    mergeTargetVendorId?: string,
  ): Promise<DedupeCandidate> {
    const { data, error } = await this.client.rpc("resolve_vendor_dedupe_candidate", {
      p_candidate_id: id,
      p_expected_version: expectedVersion,
      p_resolution: resolution,
      p_merge_target_vendor_id: mergeTargetVendorId ?? null,
    });
    return versioned(data, error) as DedupeCandidate;
  }

  async saveContact(
    vendorId: string,
    id: string | null,
    expectedVersion: number | null,
    input: ContactInput,
  ): Promise<Contact> {
    const { data, error } = await this.client.rpc("save_contact", {
      p_vendor_id: vendorId,
      p_contact_id: id,
      p_expected_version: expectedVersion,
      p_input: input,
    });
    return versioned(data, error) as Contact;
  }

  async deleteRecord(
    recordType: DeleteRecordType,
    id: string,
    expectedVersion: number,
  ): Promise<void> {
    const { error } = await this.client.rpc("delete_crm_record", {
      p_record_type: recordType,
      p_record_id: id,
      p_expected_version: expectedVersion,
    });
    throwDatabaseError(error);
  }

  async getAuditEvents(cursor: number | null = null, limit = 50): Promise<AuditEventPage> {
    const membership = await this.getMembership();
    if (membership.role !== "admin") throw new Error("Only administrators can view the audit log");
    const safeLimit = Math.min(Math.max(limit, 1), 100);

    let query = this.client
      .from("audit_events")
      .select("*")
      .eq("organization_id", membership.organizationId)
      .order("id", { ascending: false })
      .limit(safeLimit + 1);
    if (cursor !== null) query = query.lt("id", cursor);

    const [eventsResult, profiles] = await Promise.all([query, this.getProfiles()]);
    const rows = required(eventsResult.data, eventsResult.error) as Omit<AuditEvent, "actor">[];
    const pageRows = rows.slice(0, safeLimit);
    const profilesById = new Map(profiles.map((profile) => [profile.user_id, profile]));
    const events = pageRows.map((event) => ({
      ...event,
      actor: event.actor_id ? (profilesById.get(event.actor_id) ?? null) : null,
    }));

    return {
      events,
      nextCursor: rows.length > safeLimit ? (pageRows.at(-1)?.id ?? null) : null,
    };
  }

  async saveInput(input: CrmInput): Promise<Activity> {
    const orders = inputOrders(input);
    const followUps = inputFollowUps(input);
    const { data, error } = await this.client.rpc("save_crm_activity_bundle", {
      p_vendor_id: input.vendor_id,
      p_client_event_id: input.client_event_id,
      p_activity_type: input.activity_type,
      p_occurred_at: input.occurred_at,
      p_summary: input.summary,
      p_details: input.details ?? null,
      p_outcome: input.outcome ?? null,
      p_next_action: input.next_action ?? null,
      p_source: input.source,
      p_raw_transcript: input.raw_transcript ?? null,
      p_channel: input.channel ?? null,
      p_direction: input.direction ?? "not_applicable",
      p_orders: orders,
      p_follow_ups: followUps,
      p_sample_requests: input.sample_requests ?? [],
      p_quotes: input.quotes ?? [],
    });
    return this.addAuthor(required(data, error) as ActivityRow);
  }

  async saveCaptureBundle(input: CaptureBundleInput): Promise<CaptureBundleResult> {
    const organizationId = await this.getOrganizationId();
    const orders = inputOrders({ ...input, vendor_id: input.vendor_id ?? "" });
    const followUps = inputFollowUps({ ...input, vendor_id: input.vendor_id ?? "" });
    const { data, error } = await this.client.rpc("save_crm_capture_bundle_with_contact", {
      p_organization_id: organizationId,
      p_vendor_id: input.vendor_id,
      p_expected_vendor_version: input.account?.expected_version ?? null,
      p_vendor_input: input.account ? withoutUndefined(input.account.input) : null,
      p_client_event_id: input.client_event_id,
      p_activity_type: input.activity_type,
      p_occurred_at: input.occurred_at,
      p_summary: input.summary,
      p_details: input.details ?? null,
      p_outcome: input.outcome ?? null,
      p_next_action: input.next_action ?? null,
      p_source: input.source,
      p_raw_transcript: input.raw_transcript ?? null,
      p_channel: input.channel ?? null,
      p_direction: input.direction ?? "not_applicable",
      p_orders: orders,
      p_follow_ups: followUps,
      p_sample_requests: input.sample_requests ?? [],
      p_quotes: input.quotes ?? [],
      p_contact_input: input.contact ? withoutUndefined(input.contact) : null,
    });
    const bundle = required(data, error) as {
      activity: ActivityRow;
      vendor: Vendor;
    };
    return {
      activity: await this.addAuthor(bundle.activity),
      vendor: bundle.vendor,
    };
  }

  async getBreakGlassExport(): Promise<BreakGlassVendorRow[]> {
    const { data, error } = await this.client.rpc("break_glass_vendor_export");
    return required(data, error) as BreakGlassVendorRow[];
  }
}
